import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { startCase } from "lodash";
import {
  Box,
  Typography,
  Alert,
  Stack,
  Tabs,
  Tab,
  Pagination,
  CircularProgress,
  IconButton,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Popover,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  AutoFixHigh as AutoFixHighIcon,
  FilterAlt as FilterAltIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import NewFieldDialog, { FIELD_TYPE_OPTIONS } from "./NewFieldDialog";
import GenericFormFieldPanel from "./GenericFormFieldPanel";

const fieldTypeMapper = {
  PDFTextField: "text",
  PDFCheckBox: "checkboxGroup",
};

const locationConfig = {
  sign: {
    borderLeft: "3px solid #1976d2",
    chipColor: "primary",
    chipLabel: "Auto • Sign",
  },
  create: {
    borderLeft: "3px solid #2e7d32",
    chipColor: "success",
    chipLabel: "Manual • Create",
  },
};

const getLocationStyle = (location) =>
  locationConfig[location] ?? { borderLeft: "1px solid #e0e0e0" };

const getCreatedFieldId = (response) =>
  response?.data?.data?.field?.field_id ??
  response?.data?.data?.field_id ??
  response?.data?.field?.field_id ??
  response?.data?.field_id ??
  "";

const flattenFieldResults = (fields = []) =>
  fields.flatMap((field) => [
    field,
    ...(field?.children || []).map((child) => ({
      ...child,
      parentLabel: field?.field_label || field?.field_name,
      parentSearchQuery: field?.field_name || field?.field_label,
    })),
  ]);

const includeParentGroupsMatchingOptions = (
  treeFields = [],
  catalogFields = [],
  searchQuery = "",
  filter = {},
) => {
  const normalizedQuery = searchQuery.trim().toLowerCase();
  if (!normalizedQuery) return treeFields;

  const matchingParentIds = new Set(
    catalogFields
      .filter(
        (field) =>
          field?.field_type === "option" &&
          field?.parent_field_id &&
          field?.field_label?.toLowerCase().includes(normalizedQuery),
      )
      .map((field) => field.parent_field_id),
  );

  if (!matchingParentIds.size) return treeFields;

  const existingFieldIds = new Set(treeFields.map((field) => field?.field_id));
  const matchingParents = catalogFields
    .filter(
      (field) =>
        matchingParentIds.has(field?.field_id) &&
        ["checkboxGroup", "selectGroup"].includes(field?.field_type) &&
        (!filter?.location || field?.location === filter.location) &&
        (!filter?.field_type || field?.field_type === filter.field_type) &&
        !existingFieldIds.has(field?.field_id),
    )
    .map((parent) => ({
      ...parent,
      children: catalogFields.filter(
        (field) => field?.parent_field_id === parent?.field_id,
      ),
    }));

  return [...treeFields, ...matchingParents];
};

const FormFieldPanel = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  onNotify,
  loading: loadingDoc,
  getFormFieldsList,
  fieldNavigationTarget,
  onFieldNavigationComplete,
  onNavigateToField,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [openNewFieldDialog, setOpenNewFieldDialog] = useState(false);
  const [openDeleteFieldDialog, setOpenDeleteFieldDialog] = useState(false);
  const [openCreateFieldDialog, setOpenCreateFieldDialog] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  const [fields, setFields] = useState([]);
  const [searchField, setSearchField] = useState("");
  const [matchingDefaultFields, setMatchingDefaultFields] = useState([]);
  const [recentlyCreatedCount, setRecentlyCreatedCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [createFieldMessage, setCreateFieldMessage] = useState({
    type: "",
    message: "",
  });

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [crossTabResultsAnchorEl, setCrossTabResultsAnchorEl] = useState(null);
  const [filter, setFilter] = useState({});
  const [fieldView, setFieldView] = useState("general");
  const [expandedFieldIds, setExpandedFieldIds] = useState(() => new Set());
  const [scrollTarget, setScrollTarget] = useState(null);

  const debounceTimerRef = useRef(null);
  const pendingNavigationRequestRef = useRef(null);
  const fieldAccordionRefs = useRef(new Map());
  const selectedPdfTextRef = useRef("");

  useEffect(() => {
    if (error) {
      const interv = setTimeout(() => {
        setError(false);
      }, 5000);
      return () => clearTimeout(interv);
    }
  }, [error]);

  useEffect(() => {
    if (createFieldMessage) {
      const interv = setTimeout(() => {
        setCreateFieldMessage({
          type: "",
          message: "",
        });
      }, 5000);
      return () => clearTimeout(interv);
    }
  }, [createFieldMessage]);

  const getFormFields = useCallback(
    async (filter, page, searchQuery) => {
      setRecentlyCreatedCount(0);
      setLoading(true);
      setError("");

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/tree`,
          {
            currentSession,
            ...filter,
            page,
            limit: 7,
            default_page: 1,
            default_limit: 7,
            query: searchQuery,
          },
          { withCredentials: true },
        );

        if (res.data.success) {
          let fields = res.data.data?.regular_fields?.fields ?? [];

          if (searchQuery?.trim()) {
            try {
              const catalogRes = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
                { currentSession },
                { withCredentials: true },
              );

              if (catalogRes.data.success) {
                fields = includeParentGroupsMatchingOptions(
                  fields,
                  catalogRes.data.data?.fields ?? [],
                  searchQuery,
                  filter,
                );
              }
            } catch {
              // Keep the existing tree search results if option lookup fails.
            }
          }

          setFields(fields);
          setTotalPage(res.data.data?.regular_fields?.total_pages ?? 0);
          setMatchingDefaultFields(
            searchQuery?.trim()
              ? flattenFieldResults(res.data.data?.default_fields?.fields ?? [])
              : [],
          );
        }
      } catch (error) {
        setMatchingDefaultFields([]);
        setError(
          error?.response?.data?.message ||
            "An error occurred while fetching fields",
        );
      } finally {
        setLoading(false);
      }
    },
    [currentSession],
  );

  const handlePageChange = (e, value) => {
    setPage(value);
    getFormFields(filter, value, searchField);
  };

  useEffect(() => {
    if (
      fieldNavigationTarget?.tabValue === "2" &&
      (fieldNavigationTarget?.fieldId || fieldNavigationTarget?.searchOnly)
    ) {
      return;
    }

    getFormFields({}, 1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isSearchOnly = fieldNavigationTarget?.searchOnly;

    if (
      fieldNavigationTarget?.tabValue !== "2" ||
      (!fieldNavigationTarget?.fieldId && !isSearchOnly)
    ) {
      return;
    }

    const searchQuery = fieldNavigationTarget.searchQuery || "";

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    pendingNavigationRequestRef.current = isSearchOnly
      ? null
      : fieldNavigationTarget.requestId;
    setRecentlyCreatedCount(0);
    setCrossTabResultsAnchorEl(null);
    setSearchField(searchQuery);
    setPage(1);
    getFormFields({}, 1, searchQuery);

    if (isSearchOnly) {
      onFieldNavigationComplete?.(fieldNavigationTarget.requestId);
    }
  }, [fieldNavigationTarget, getFormFields, onFieldNavigationComplete]);

  useEffect(() => {
    if (
      loading ||
      !fieldNavigationTarget?.fieldId ||
      pendingNavigationRequestRef.current !== fieldNavigationTarget.requestId
    ) {
      return;
    }

    const targetId = fieldNavigationTarget.fieldId;
    let targetPath = null;

    for (const field of fields || []) {
      if (field?.field_id === targetId) {
        targetPath = [field.field_id];
        break;
      }

      const child = field?.children?.find(
        (option) => option?.field_id === targetId,
      );
      if (child) {
        targetPath = [field.field_id, child.field_id];
        break;
      }
    }

    if (!targetPath) return;

    setExpandedFieldIds((previousIds) => {
      const nextIds = new Set(previousIds);
      targetPath.forEach((fieldId) => nextIds.add(fieldId));
      return nextIds;
    });
    setScrollTarget({
      fieldId: targetId,
      requestId: fieldNavigationTarget.requestId,
    });
    pendingNavigationRequestRef.current = null;
  }, [fieldNavigationTarget, fields, loading]);

  useEffect(() => {
    if (!scrollTarget?.fieldId) return;

    const timerId = window.setTimeout(() => {
      fieldAccordionRefs.current.get(scrollTarget.fieldId)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      onFieldNavigationComplete?.(scrollTarget.requestId);
      setScrollTarget(null);
    }, 350);

    return () => window.clearTimeout(timerId);
  }, [expandedFieldIds, onFieldNavigationComplete, scrollTarget]);

  const handleAccordionChange = (fieldId) => (event, isExpanded) => {
    setExpandedFieldIds((previousIds) => {
      const nextIds = new Set(previousIds);

      if (isExpanded) {
        nextIds.add(fieldId);
      } else {
        nextIds.delete(fieldId);
      }

      return nextIds;
    });
  };

  const handleCloseNewFieldDialog = () => {
    setOpenNewFieldDialog(false);
    setSelectedField(null);
  };

  const handleCloseDeleteFieldDialog = () => {
    setOpenDeleteFieldDialog(false);
    setSelectedField(null);
  };

  const handleCloseCreateFieldDialog = () => {
    setOpenCreateFieldDialog(false);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError("");
    const deletedFieldId = selectedField?.field_id;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/delete`,
        {
          field_id: deletedFieldId,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
        },
      );

      if (response?.data?.success) {
        setCurrentProcessingFile((prevFile) => {
          const tempFields = prevFile?.document?.tempFields;
          if (!tempFields || !deletedFieldId) return prevFile;

          const untagDeletedField = (field) =>
            field?.field_id === deletedFieldId
              ? {
                  ...field,
                  field_id: "",
                  location: "",
                }
              : field;

          return {
            ...prevFile,
            document: {
              ...prevFile.document,
              tempFields: {
                ...tempFields,
                formFields: tempFields.formFields?.map(untagDeletedField),
                selectedExtraFields:
                  tempFields.selectedExtraFields?.map(untagDeletedField),
                selectedFormField: untagDeletedField(
                  tempFields.selectedFormField,
                ),
              },
            },
          };
        });

        getFormFields(filter, page, searchField.trim());
        getFormFieldsList();
        handleCloseDeleteFieldDialog();
      }
    } catch (error) {
      setError(
        error?.response?.data?.message ||
          `An error occurred while deleting field`,
      );
      handleCloseDeleteFieldDialog();
    } finally {
      setLoading(false);
    }
  };

  const handleAttach = (e, field) => {
    e.stopPropagation();

    const tempFields = currentProcessingFile?.document?.tempFields;

    if (!tempFields?.selectedExtraFields?.length) {
      onNotify?.(
        "error",
        `Select a field from the PDF first, then you'll be able to assign it.`,
      );
      return;
    }

    if (tempFields?.selectedExtraFields?.length > 1) {
      onNotify?.(
        "error",
        `You have selected more than one field. Only a single field can be assigned at a time.`,
      );
      return;
    }

    const selectedField = tempFields?.selectedExtraFields?.[0] ?? {};

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (selectedField?.name === f?.name) {
        return {
          ...f,
          ...(!f.hidden && { field_label: field?.field_label }),
          field_id: field?.field_id,
          location: field?.location || "create",
          ...(field?.field_type === "percentageCheckbox" && { hidden: false }),
        };
      } else {
        return f;
      }
    });
    const updatedSelectedFields = [
      {
        ...selectedField,
        ...(!field.hidden && { field_label: field?.field_label }),
        field_id: field?.field_id,
        location: field?.location || "create",
        ...(field?.field_type === "percentageCheckbox" && { hidden: false }),
      },
    ];

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          selectedFormField: updatedSelectedFields[0],
          formFields: updatedFormFields,
          selectedExtraFields: updatedSelectedFields,
        },
      },
    }));
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setRecentlyCreatedCount(0);
    setSearchField(val);
    setCrossTabResultsAnchorEl(null);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      getFormFields(filter, 1, val.trim());
      setPage(1);
    }, 1000);
  };

  const handleCreateSelectedFields = async () => {
    setLoading(true);
    setCreateFieldMessage({ type: "", message: "" });

    const tempFields = currentProcessingFile?.document?.tempFields;
    const fieldsArray = tempFields?.selectedExtraFields;

    const succeeded = [];
    const failed = [];
    const total = fieldsArray.length;
    for (let i = 0; i < total; i++) {
      const fieldValues = fieldsArray[i];
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/upsert`,
          {
            currentSession,
            field: {
              field_label:
                fieldValues?.field_label ??
                fieldValues?.label ??
                fieldValues?.name,
              field_type: fieldTypeMapper[fieldValues?.type],
              location: "create",
            },
          },
          { withCredentials: true },
        );
        if (response?.data?.success) {
          succeeded.push({
            index: i,
            field: fieldValues,
            data: response.data,
            createdField: {
              field_id: getCreatedFieldId(response),
              field_label:
                fieldValues?.field_label ??
                fieldValues?.label ??
                fieldValues?.name,
              field_type: fieldTypeMapper[fieldValues?.type],
              location: "create",
            },
          });
        } else {
          const err = {
            index: i,
            field: fieldValues,
            message: response?.data?.message || "Unknown error",
          };
          failed.push(err);
        }
      } catch (error) {
        const err = {
          index: i,
          field: fieldValues,
          message:
            error?.response?.data?.message ||
            `An error occurred while creating field`,
        };
        failed.push(err);
      }
    }
    // Refresh fields list once after all iterations
    if (succeeded.length > 0) {
      const createdFieldsByName = new Map(
        succeeded
          .filter(
            ({ field, createdField }) => field?.name && createdField?.field_id,
          )
          .map(({ field, createdField }) => [field.name, createdField]),
      );

      if (createdFieldsByName.size) {
        setCurrentProcessingFile((prevFile) => {
          const prevTempFields = prevFile?.document?.tempFields;
          if (!prevTempFields) return prevFile;

          const updateTaggedField = (field) => {
            const matchingCreatedField = createdFieldsByName.get(field?.name);

            return matchingCreatedField
              ? {
                  ...field,
                  field_label: matchingCreatedField.field_label,
                  field_id: matchingCreatedField.field_id,
                  location: matchingCreatedField.location,
                }
              : field;
          };

          return {
            ...prevFile,
            document: {
              ...prevFile.document,
              tempFields: {
                ...prevTempFields,
                formFields: prevTempFields.formFields?.map(updateTaggedField),
                selectedExtraFields:
                  prevTempFields.selectedExtraFields?.map(updateTaggedField),
                selectedFormField: updateTaggedField(
                  prevTempFields.selectedFormField,
                ),
              },
            },
          };
        });
      }

      const createdFields = succeeded
        .map(({ createdField }) => createdField)
        .filter((field) => field?.field_id);
      let recentlyCreatedFields = createdFields;

      try {
        const fieldsResponse = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
          { currentSession },
          { withCredentials: true },
        );

        if (fieldsResponse?.data?.success) {
          const catalogById = new Map(
            (fieldsResponse.data.data?.fields ?? [])
              .filter((field) => field?.field_id)
              .map((field) => [field.field_id, field]),
          );

          recentlyCreatedFields = createdFields.map(
            (field) => catalogById.get(field.field_id) || field,
          );
        }
      } catch {
        // The successful upsert responses still provide enough data to show
        // every newly created field if the catalog refresh is unavailable.
      }

      setFilter({});
      setSearchField("");
      setMatchingDefaultFields([]);
      setFields(recentlyCreatedFields);
      setTotalPage(recentlyCreatedFields.length > 0 ? 1 : 0);
      setRecentlyCreatedCount(recentlyCreatedFields.length);
      setPage(1);
      getFormFieldsList();
    }
    // Report a summary message
    if (failed.length === 0) {
      setCreateFieldMessage({
        type: "success",
        message: `All ${succeeded.length} field(s) created successfully.`,
      });
    } else if (succeeded.length === 0) {
      setCreateFieldMessage({
        type: "error",
        message: `All ${failed.length} field(s) failed. First error: ${failed[0].message}`,
      });
    } else {
      setCreateFieldMessage({
        type: "warning",
        message: `${succeeded.length} succeeded, ${failed.length} failed. First error: ${failed[0].message}`,
      });
    }

    handleCloseCreateFieldDialog();
    setLoading(false);
  };

  const handleClickFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{ borderBottom: "1px solid #F3F4F6", bgcolor: "background.paper" }}
      >
        <Tabs
          value={fieldView}
          onChange={(event, newValue) => setFieldView(newValue)}
          variant="fullWidth"
        >
          <Tab
            label="General Fields"
            value="general"
            sx={{ textTransform: "none" }}
          />
          <Tab
            label="Generic Fields"
            value="4"
            sx={{ margin: "0px !important" }}
          />
        </Tabs>
      </Box>

      {fieldView === "general" ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100% - 98px)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: "1px solid #F3F4F6",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          ></Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              px: 1.5,
              py: 1.25,
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<AutoFixHighIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                setOpenCreateFieldDialog(true);
              }}
              color="primary"
              disabled={
                !currentProcessingFile?.document?.tempFields
                  ?.selectedExtraFields?.length || loadingDoc
              }
            >
              Auto Create Field
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={loadingDoc}
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onMouseDown={() => {
                selectedPdfTextRef.current =
                  window
                    .getSelection()
                    ?.toString()
                    .replace(/\s+/g, " ")
                    .trim() || "";
              }}
              onClick={() => {
                const tempFields = currentProcessingFile?.document?.tempFields;
                const overlaySelectedText = [
                  tempFields?.selectedPdfTextItem,
                  ...(tempFields?.selectedExtraPdfTextItems || []),
                ]
                  .map((item) => item?.text?.replace(/\s+/g, " ").trim())
                  .filter(Boolean)
                  .join(" ");
                const selectedText =
                  selectedPdfTextRef.current || overlaySelectedText;

                setSelectedField(
                  selectedText ? { field_label: selectedText } : null,
                );
                setOpenNewFieldDialog(true);
              }}
              color="primary"
            >
              New Field
            </Button>
          </Box>
          <Box
            display={"flex"}
            alignItems={"center"}
            gap={1}
            sx={{ px: 1.5, pb: 1.25 }}
          >
            <TextField
              id="searchInput"
              value={searchField}
              onChange={(e) => handleChange(e)}
              size="small"
              placeholder="Search assigned fields..."
              sx={{
                "& .MuiInputBase-input": { fontSize: 14 },
                flex: 1,
              }}
              InputProps={{
                sx: { padding: "0px 10px" },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
            <IconButton
              size="small"
              onClick={handleClickFilter}
              disabled={loadingDoc}
              sx={{
                border: "1px solid #d0d7de",
                borderRadius: 1.5,
                backgroundColor: "transparent",
                p: "4.41px !important",
              }}
            >
              <FilterAltIcon
                sx={{
                  fontSize: "0.95rem",
                }}
              />
            </IconButton>
          </Box>
          {recentlyCreatedCount > 0 && (
            <Box sx={{ px: 1.5, pb: 1.25 }}>
              <Chip
                label={`${recentlyCreatedCount} recently created ${
                  recentlyCreatedCount === 1 ? "field" : "fields"
                }`}
                size="small"
                color="success"
                variant="outlined"
              />
            </Box>
          )}
          {matchingDefaultFields.length > 0 && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={(event) =>
                  setCrossTabResultsAnchorEl(event.currentTarget)
                }
                sx={{
                  mx: 1.5,
                  mb: 1.25,
                  px: 1.25,
                  justifyContent: "space-between",
                  textTransform: "none",
                  fontSize: 12,
                }}
              >
                {matchingDefaultFields.length} matching{" "}
                {matchingDefaultFields.length === 1 ? "field" : "fields"} in
                Default Fields
                <Box component="span" sx={{ ml: 1, fontSize: 16 }}>
                  ›
                </Box>
              </Button>
              <Popover
                open={Boolean(crossTabResultsAnchorEl)}
                anchorEl={crossTabResultsAnchorEl}
                onClose={() => setCrossTabResultsAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    width: 300,
                    maxWidth: "calc(100vw - 32px)",
                    maxHeight: 280,
                  },
                }}
              >
                <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Matching Default Fields
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 11 }}>
                    Select a field to open it in the Default Fields tab.
                  </Typography>
                </Box>
                <Stack sx={{ px: 0.75, pb: 0.75 }}>
                  {matchingDefaultFields.map((field) => (
                    <Button
                      key={field?.field_id}
                      size="small"
                      variant="text"
                      onClick={() => {
                        setCrossTabResultsAnchorEl(null);
                        onNavigateToField?.({
                          fieldId: field?.field_id,
                          searchQuery:
                            field?.parentSearchQuery ||
                            field?.field_name ||
                            field?.field_label ||
                            searchField,
                          tabValue: "3",
                        });
                      }}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        textTransform: "none",
                        px: 1,
                        py: 0.75,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {field?.field_label || field?.field_name}
                        </Typography>
                        {field?.parentLabel && (
                          <Typography
                            color="text.secondary"
                            sx={{
                              fontSize: 10,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {field.parentLabel}
                          </Typography>
                        )}
                      </Box>
                    </Button>
                  ))}
                </Stack>
              </Popover>
            </>
          )}
          {(error || createFieldMessage?.message) && (
            <Alert
              severity={error ? "error" : createFieldMessage?.type}
              sx={{ borderRadius: 2, mx: 1.5, mb: 1.25 }}
            >
              {error || createFieldMessage?.message}
            </Alert>
          )}

          {loading ? (
            <Box
              sx={{
                px: 1.5,
                display: "flex",
                justifyContent: "center",
                mt: 2.5,
              }}
            >
              <CircularProgress color="warning" size="3rem" />
            </Box>
          ) : fields?.length === 0 ? (
            <Box sx={{ px: 1.5 }}>
              <Typography
                color="primary"
                variant="h6"
                mb={2}
                mt={2.5}
                sx={{ fontSize: "1rem" }}
                align="center"
              >
                You have no fields to display just yet
              </Typography>
            </Box>
          ) : (
            <>
              <Box flex={1} sx={{ px: 1.5, minHeight: 0, overflowY: "auto" }}>
                <Stack gap={0.75}>
                  {fields?.map((field, index) => {
                    return (
                      <Accordion
                        key={field?.field_id}
                        ref={(node) => {
                          if (node) {
                            fieldAccordionRefs.current.set(
                              field?.field_id,
                              node,
                            );
                          } else {
                            fieldAccordionRefs.current.delete(field?.field_id);
                          }
                        }}
                        expanded={expandedFieldIds.has(field?.field_id)}
                        onChange={handleAccordionChange(field?.field_id)}
                        disableGutters
                        elevation={0}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "12px !important",
                          overflow: "hidden",
                          borderLeft: getLocationStyle(field?.location)
                            .borderLeft,
                          "&:before": { display: "none" },
                          "&.Mui-expanded": {
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <AccordionSummary
                          component="div"
                          expandIcon={
                            <ExpandMoreIcon sx={{ fontSize: "1.5rem" }} />
                          }
                          sx={{
                            gap: 2,
                          }}
                          slotProps={{
                            content: {
                              sx: {
                                display: "flex",
                                flexDirection: "column",
                              },
                            },
                          }}
                        >
                          <Box
                            display={"flex"}
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontSize: "0.85rem",
                                  color: "rgba(0, 0, 0, 0.8)",
                                  fontWeight: 500,
                                }}
                              >
                                {field?.field_label}
                              </Typography>
                              <Box
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={0.5}
                                sx={{
                                  mt: 0.5,
                                  minHeight: 20,
                                }}
                              >
                                {field?.location && (
                                  <Chip
                                    label={startCase(field.location)}
                                    size="small"
                                    variant="filled"
                                    color={
                                      getLocationStyle(field.location).chipColor
                                    }
                                    sx={{
                                      fontSize: "0.7rem",
                                      height: 20,
                                      borderRadius: 1,
                                    }}
                                  />
                                )}

                                {field?.field_type &&
                                  !expandedFieldIds.has(field?.field_id) && (
                                    <Chip
                                      label={startCase(field.field_type)}
                                      size="small"
                                      variant="filled"
                                      sx={{
                                        fontSize: "0.7rem",
                                        height: 20,
                                        borderRadius: 1,
                                      }}
                                    />
                                  )}
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={(e) => handleAttach(e, field)}
                              sx={{
                                border: "1px solid #d0d7de",
                                borderRadius: 1.5,
                                backgroundColor: "transparent",
                                p: "4.41px !important",
                              }}
                              disabled={loadingDoc}
                            >
                              <AttachFileIcon
                                sx={{
                                  fontSize: "0.95rem",
                                  transform: "rotate(45deg)",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.75,
                              flexWrap: "wrap",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box display={"flex"} alignItems={"center"} gap={1}>
                              {field?.field_type && (
                                <Chip
                                  label={startCase(field.field_type)}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    fontSize: "0.7rem",
                                    height: 20,
                                    borderRadius: 1,
                                  }}
                                />
                              )}
                            </Box>
                            <Box display={"flex"} alignItems={"center"} gap={1}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenNewFieldDialog(true);
                                  setSelectedField(field);
                                }}
                                disabled={loadingDoc}
                                sx={{
                                  border: "1px solid #d0d7de",
                                  borderRadius: 1.5,
                                  backgroundColor: "transparent",
                                  p: "4px !important",
                                }}
                              >
                                <EditIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDeleteFieldDialog(true);
                                  setSelectedField(field);
                                }}
                                disabled={loadingDoc}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "error.light",
                                  borderRadius: 1.5,
                                  backgroundColor: "transparent",
                                  p: "4px !important",
                                }}
                              >
                                <DeleteIcon
                                  sx={{ fontSize: "1rem" }}
                                  color="error"
                                />
                              </IconButton>
                            </Box>
                          </Box>
                          {["checkboxGroup", "selectGroup"].includes(
                            field?.field_type,
                          ) &&
                            field?.children?.length > 0 && (
                              <Box
                                sx={{
                                  mt: 1.25,
                                  pt: 1.25,
                                  borderTop: "1px dashed #e0e0e0",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "rgba(0, 0, 0, 0.45)",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                    mb: 0.75,
                                    display: "block",
                                  }}
                                >
                                  Options
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5,
                                  }}
                                >
                                  {field?.children.map((option, index) => (
                                    <Accordion
                                      key={option?.field_id ?? index}
                                      ref={(node) => {
                                        if (node) {
                                          fieldAccordionRefs.current.set(
                                            option?.field_id,
                                            node,
                                          );
                                        } else {
                                          fieldAccordionRefs.current.delete(
                                            option?.field_id,
                                          );
                                        }
                                      }}
                                      expanded={expandedFieldIds.has(
                                        option?.field_id,
                                      )}
                                      onChange={handleAccordionChange(
                                        option?.field_id,
                                      )}
                                      disableGutters
                                      elevation={0}
                                      sx={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "8px !important",
                                        overflow: "hidden",
                                        "&:before": { display: "none" },
                                        "&.Mui-expanded": {
                                          boxShadow:
                                            "0 2px 8px rgba(0,0,0,0.08)",
                                        },
                                      }}
                                    >
                                      <AccordionSummary
                                        expandIcon={
                                          <ExpandMoreIcon
                                            sx={{ fontSize: "1.2rem" }}
                                          />
                                        }
                                        sx={{
                                          gap: 1.5,
                                          minHeight: "36px !important",
                                        }}
                                        slotProps={{
                                          content: {
                                            sx: {
                                              display: "flex",
                                              flexDirection: "column",
                                              my: "8px !important",
                                            },
                                          },
                                        }}
                                      >
                                        <Box
                                          display="flex"
                                          justifyContent={"space-between"}
                                          alignItems="center"
                                          gap={0.5}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.78rem",
                                              color: "rgba(0, 0, 0, 0.8)",
                                              fontWeight: 500,
                                              lineHeight: 1.4,
                                            }}
                                          >
                                            {option?.field_label}
                                          </Typography>
                                        </Box>
                                      </AccordionSummary>

                                      <AccordionDetails
                                        sx={{ pt: 0.5, pb: 1, px: 1.5 }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 0.75,
                                            flexWrap: "wrap",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                          }}
                                        >
                                          {option?.location && (
                                            <Chip
                                              label={startCase(option.location)}
                                              size="small"
                                              variant="filled"
                                              color={
                                                getLocationStyle(
                                                  option.location,
                                                ).chipColor
                                              }
                                              sx={{
                                                fontSize: "0.7rem",
                                                height: 20,
                                                borderRadius: 1,
                                              }}
                                            />
                                          )}
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            justifyContent="flex-end"
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAttach(e, option);
                                              }}
                                              disabled={loadingDoc}
                                              sx={{
                                                border: "1px solid #d0d7de",
                                                borderRadius: 1.5,
                                                backgroundColor: "transparent",
                                                p: "4px !important",
                                              }}
                                            >
                                              <AttachFileIcon
                                                sx={{
                                                  fontSize: "0.85rem",
                                                  transform: "rotate(45deg)",
                                                }}
                                              />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedField(option);
                                                setOpenNewFieldDialog(true);
                                              }}
                                              disabled={loadingDoc}
                                              sx={{
                                                border: "1px solid #d0d7de",
                                                borderRadius: 1.5,
                                                backgroundColor: "transparent",
                                                p: "4px !important",
                                              }}
                                            >
                                              <EditIcon
                                                sx={{ fontSize: "0.85rem" }}
                                              />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenDeleteFieldDialog(true);
                                                setSelectedField(option);
                                              }}
                                              disabled={loadingDoc}
                                              sx={{
                                                border: "1px solid",
                                                borderColor: "error.light",
                                                borderRadius: 1.5,
                                                backgroundColor: "transparent",
                                                p: "4px !important",
                                              }}
                                            >
                                              <DeleteIcon
                                                sx={{ fontSize: "0.85rem" }}
                                                color="error"
                                              />
                                            </IconButton>
                                          </Box>
                                        </Box>
                                      </AccordionDetails>
                                    </Accordion>
                                  ))}
                                </Box>
                              </Box>
                            )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              </Box>
              <Box sx={{ py: 1.5, display: "flex", justifyContent: "center" }}>
                <Pagination
                  count={totalPage}
                  page={page}
                  onChange={handlePageChange}
                  siblingCount={1}
                  boundaryCount={1}
                  size={"small"}
                />
              </Box>
            </>
          )}
        </Box>
      ) : (
        <GenericFormFieldPanel
          currentProcessingFile={currentProcessingFile}
          setCurrentProcessingFile={setCurrentProcessingFile}
          onNotify={onNotify}
          loading={loading}
        />
      )}
      {openNewFieldDialog && (
        <NewFieldDialog
          open={openNewFieldDialog}
          handleClose={handleCloseNewFieldDialog}
          getFields={getFormFields}
          selectedField={selectedField}
          searchField={searchField}
          fields={fields}
          filter={filter}
          setPage={setPage}
          getFormFieldsList={getFormFieldsList}
          onFieldCreated={setSearchField}
        />
      )}
      <Dialog
        open={openDeleteFieldDialog}
        onClose={handleCloseDeleteFieldDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove{" "}
            <strong>{selectedField?.field_label}</strong>{" "}
            {selectedField?.field_type === "option" ? "option" : "field"}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteFieldDialog}
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDeleteConfirm}
            loading={loading}
            variant="contained"
            color="error"
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openCreateFieldDialog}
        onClose={handleCloseCreateFieldDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to create selected fields?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCreateFieldDialog}
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleCreateSelectedFields}
            loading={loading}
            variant="contained"
            color="error"
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Popover
        id={Boolean(filterAnchorEl) ? "simple-popover" : undefined}
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Stack p={2} gap={1}>
          <Typography sx={{ fontWeight: 500 }}>Filter</Typography>
          <TextField
            label="Location"
            size="small"
            disabled={loading}
            select
            value={filter?.location ?? ""}
            sx={{ minWidth: "200px" }}
            onChange={(event) => {
              setFilter((prev) => ({ ...prev, location: event.target.value }));
            }}
          >
            <MenuItem value="create">Create</MenuItem>
            <MenuItem value="sign">Sign</MenuItem>
          </TextField>
          <TextField
            label="Field Type"
            size="small"
            disabled={loading}
            select
            value={filter?.field_type ?? ""}
            sx={{ minWidth: "200px" }}
            onChange={(event) => {
              setFilter((prev) => ({
                ...prev,
                field_type: event.target.value,
              }));
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: { maxHeight: "320px" },
                },
              },
            }}
          >
            {FIELD_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={loading}
              onClick={() => {
                setFilter({});
                getFormFields({}, 1, searchField);
                setPage(1);
                handleCloseFilter();
              }}
              color="primary"
            >
              Clear Filter
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={loading}
              onClick={() => {
                getFormFields(filter, 1, searchField.trim());
                setPage(1);
                handleCloseFilter();
              }}
              color="primary"
            >
              Apply
            </Button>
          </Box>
        </Stack>
      </Popover>
    </>
  );
};

export default FormFieldPanel;
